import { Configuration, PopupRequest, LogLevel } from "@azure/msal-browser";

// Read Vite env vars for production; fallback to window origin for dev
const env = import.meta.env;
const getRedirectUri = () => {
    // Check multiple conditions to determine if we're in local development
    const isLocalDevelopment = typeof window !== "undefined" && 
                              window.location.origin.includes('localhost') && 
                              env.DEV === true && 
                              env.MODE === 'development' &&
                              // Additional safety check - not in production environment
                              env.NODE_ENV !== 'production';
    
    // Only use localhost if ALL conditions are met for local development
    if (isLocalDevelopment) {
        return window.location.origin;
    }
    
    // For production/deployed builds, use the current window origin
    return typeof window !== "undefined" ? window.location.origin : "https://www.rrrealty.ai";
};

const CLIENT_ID = env.VITE_AUTH_CLIENT_ID || "d4c452c4-5324-40ff-b43b-25f3daa2a45c";
const AUTHORITY = env.VITE_AUTH_AUTHORITY || "https://login.microsoftonline.com/99848873-e61d-44cc-9862-d05151c567ab";
const API_SCOPE = env.VITE_API_SCOPE || `api://${CLIENT_ID}/access_as_user`;

// MSAL configuration
export const msalConfig: Configuration = {
    auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY,
        redirectUri: getRedirectUri(),
        postLogoutRedirectUri: getRedirectUri(),
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: true,
        // `temporaryCacheLocation` is deliberately left at its sessionStorage
        // default. Moving it to localStorage looks like it would harden the
        // redirect against storage loss, but it makes a wedged
        // `interaction_in_progress` survive browser restarts — turning an
        // intermittent fault into a permanent lockout. storeAuthStateInCookie
        // above already mirrors temp state into cookies (read first), so the
        // cross-tab durability is there without the lockout. See
        // utils/authRecovery.ts.
    },
    system: {
        allowRedirectInIframe: false,
        windowHashTimeout: 9000,
        iframeHashTimeout: 9000,
        loadFrameTimeout: 9000,
        // MSAL was previously silent, so a failed redirect left no trace at all.
        // Warning-and-above only, and PII strictly off: MSAL's verbose output
        // includes URLs that can carry tokens.
        loggerOptions: {
            piiLoggingEnabled: false,
            logLevel: LogLevel.Warning,
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                if (containsPii) return;
                if (level === LogLevel.Error) {
                    console.error(`[msal] ${message}`);
                } else if (level === LogLevel.Warning) {
                    console.warn(`[msal] ${message}`);
                }
            },
        },
    },
};

// Microsoft Graph API scopes (read-only, admin consent granted)
export const graphScopes = {
    mail: "https://graph.microsoft.com/Mail.Read",
    calendar: "https://graph.microsoft.com/Calendars.Read",
    schedule: "https://graph.microsoft.com/Schedule.Read.All",
    people: "https://graph.microsoft.com/People.Read",
};

// Scopes for login (request API access + openid profile + groups)
export const loginRequest: PopupRequest = {
    scopes: [API_SCOPE, "openid", "profile", "email"],
    extraQueryParameters: {
        // Request groups in ID token
        claims: JSON.stringify({
            "id_token": {
                "groups": null
            }
        })
    }
};

// Scopes for Graph API access (requested separately when needed)
export const graphRequest: PopupRequest = {
    scopes: [graphScopes.mail, graphScopes.calendar, graphScopes.schedule, graphScopes.people],
};

// Graph API configuration
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
    graphMailEndpoint: "https://graph.microsoft.com/v1.0/me/messages",
    graphCalendarEndpoint: "https://graph.microsoft.com/v1.0/me/calendarView",
    graphFindMeetingTimesEndpoint: "https://graph.microsoft.com/v1.0/me/findMeetingTimes",
};

// Backend API configuration
export const apiConfig = {
    scopes: [API_SCOPE],
    uri: "/api",
};
